import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  fromAdUser,
  adUserSelectors,
  userProfileHelpers,
  UserProfile
} from '@content-platform/web-angular-administration-shared-data-access-graph-api';
import { endpointNeedsAdalToken } from '../helpers/endpoint-helper';
import { ContentPlatformUserHeader } from '../models/content-platform-user';
import { filter } from 'rxjs/operators';
import { DebugFlagsService } from '@content-platform/web-angular-shared-util-development';
import { IpAddressService } from '@content-platform/web-angular-shared-util-logging';
import { REMOVE_CONTENT_PLATFORM_USER_HEADER } from '../debug-flags';
/**
 * Interceptor for adding user details to outgoing requests
 *
 * @export
 * @class ContentPlatformUserService
 */
@Injectable()
export class ContentPlatformUserService implements HttpInterceptor {
  protected userHeaderInfo: ContentPlatformUserHeader = {};

  private gotUserInfo = false;
  private hasRequestedIpAddress = false;
  constructor(
    protected store: Store<fromAdUser.AdUserState>,
    protected ipAddressService: IpAddressService,
    protected debugFlags: DebugFlagsService
  ) {
    this.store
      .pipe(
        select(adUserSelectors.getCurrentUserProfile),
        filter(currentUser => !!currentUser)
      )
      .subscribe(currentUser => {
        this.gotUserInfo = true;
        this.userHeaderInfo = this.getUserHeader(currentUser);
      });
  }

  protected getUserHeader(currentUser: UserProfile): ContentPlatformUserHeader {
    return {
      ...this.userHeaderInfo,
      userName: currentUser.displayName,
      email: currentUser.mail,
      userType: userProfileHelpers.getUserType(currentUser),
      id: currentUser.objectId
    };
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // As this request must have happened after app bootstrap, we can safely request the IP address
    this.requestIPAddressForHeaderInfoIfNeeded();
    if (
      this.debugFlags.hasDebugFlag(REMOVE_CONTENT_PLATFORM_USER_HEADER) ||
      !endpointNeedsAdalToken(request) ||
      !this.gotUserInfo
    ) {
      return next.handle(request);
    }

    return next.handle(this.setContentPlatforUserHeader(request));
  }

  protected setContentPlatforUserHeader(request: HttpRequest<any>) {
    const userHeaderInfo = JSON.parse(JSON.stringify(this.userHeaderInfo));

    userHeaderInfo.userName = this.normalizeText(userHeaderInfo.userName);
    return request.clone({
      setHeaders: {
        'Content-Platform-User': JSON.stringify(userHeaderInfo)
      }
    });
  }

  /**
   * This method should NOT be called from the constructor, as that causes a circular dependency
   *
   * @private
   * @returns {void}
   * @memberof ContentPlatformUserService
   */
  private requestIPAddressForHeaderInfoIfNeeded(): void {
    if (
      this.hasRequestedIpAddress ||
      !this.ipAddressService ||
      !this.ipAddressService.ipAddressAsync
    ) {
      // Either the IP address service has not initialized yet and we can't subscribe to the ipAddress observable, or
      // we've already initiated a request to get the IP address, don't do it again
      return;
    }
    this.hasRequestedIpAddress = true;
    this.ipAddressService.ipAddressAsync.subscribe(ipAddress => {
      this.userHeaderInfo = {
        ...this.userHeaderInfo,
        ipAddress
      };
    });
  }

  /**
   * This method is to normalize string
   *
   * @private
   * @returns {string}
   * @memberof ContentPlatformUserService
   */
   private normalizeText(text): string {
    return text.normalize('NFKD').replace(/[^\w, ]/g, '');
  }
}
